import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { Message, MessageConnection } from '../../models/chat.models';
import { format } from 'date-fns';

import {
    LoadMessages,
    LoadMessagesSuccess,
    LoadMessagesError,
    LoadMoreMessages,
    LoadMoreMessagesSuccess,
    LoadMoreMessagesError,
    SendMessage,
    SendMessageSuccess,
    SendMessageError,
    ClearMessagesForChannel,
    AddMessage,
} from '../actions/message.actions';
import { UpdateChannel } from '../actions/channel.actions';
import { CurrentUserModel } from '@shared/common/models';

export interface MessageStateModel {
    byChannel: {
        [channelId: string]: {
            items: Message[];
            nextToken?: string;
            loading: boolean;
            error: unknown;
            lastUpdated: number;
        };
    };
}

@State<MessageStateModel>({
    name: 'messages',
    defaults: {
        byChannel: {},
    },
})
@Injectable()
export class MessageState {
    private currentUser = inject(CurrentUserModel);
    constructor(
        private chatService: ChatService,
        private store: Store,
    ) {}

    @Selector()
    static getMessages(state: MessageStateModel | undefined) {
        return (channelId: string): Message[] => state?.byChannel?.[channelId]?.items ?? [];
    }

    @Selector()
    static getNextToken(state: MessageStateModel | undefined) {
        return (channelId: string): string | undefined => state?.byChannel?.[channelId]?.nextToken;
    }

    @Selector()
    static isLoading(state: MessageStateModel | undefined) {
        return (channelId: string): boolean => !!state?.byChannel?.[channelId]?.loading;
    }

    @Action(LoadMessages)
    loadMessages(ctx: StateContext<MessageStateModel>, action: LoadMessages) {
        this.ensureChannel(ctx, action.channelId);
        this.patchChannel(ctx, action.channelId, { loading: true, error: null });

        return this.chatService.getMessages(action.channelId, action.limit, action.nextToken).pipe(
            tap((connection: MessageConnection) =>
                ctx.dispatch(
                    new LoadMessagesSuccess(action.channelId, connection, action.nextToken),
                ),
            ),
            catchError((error) => {
                ctx.dispatch(new LoadMessagesError(action.channelId, error));
                return of(error);
            }),
        );
    }

    @Action(LoadMessagesSuccess)
    loadMessagesSuccess(ctx: StateContext<MessageStateModel>, action: LoadMessagesSuccess) {
        const shouldAppend = !!action.requestNextToken;
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId] ?? {
            items: [],
            nextToken: undefined,
            loading: false,
            error: null,
            lastUpdated: 0,
        };

        const baseItems = shouldAppend ? slice.items : [];
        const existingIds = new Set(baseItems.map((m) => m._id));
        const deduped = action.connection.items.filter((m) => !existingIds.has(m._id));

        const merged = this.computeGroupedMessages([...baseItems, ...deduped]);

        this.patchChannel(ctx, action.channelId, {
            items: merged,
            nextToken: action.connection.nextToken,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadMessagesError)
    loadMessagesError(ctx: StateContext<MessageStateModel>, action: LoadMessagesError) {
        this.patchChannel(ctx, action.channelId, { loading: false, error: action.error });
    }

    @Action(LoadMoreMessages)
    loadMoreMessages(ctx: StateContext<MessageStateModel>, action: LoadMoreMessages) {
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId];
        const requestNextToken = slice?.nextToken;

        // Gate if no more pages known
        if (!requestNextToken && requestNextToken !== '') return;

        this.patchChannel(ctx, action.channelId, { loading: true, error: null });

        return this.chatService.getMessages(action.channelId, action.limit, requestNextToken).pipe(
            tap((connection) =>
                ctx.dispatch(
                    new LoadMoreMessagesSuccess(action.channelId, connection, requestNextToken),
                ),
            ),
            catchError((error) => {
                ctx.dispatch(new LoadMoreMessagesError(action.channelId, error));
                return of(error);
            }),
        );
    }

    @Action(LoadMoreMessagesSuccess)
    loadMoreMessagesSuccess(ctx: StateContext<MessageStateModel>, action: LoadMoreMessagesSuccess) {
        // Handled by LoadMessagesSuccess logic, but keep for parity with channel if you prefer:
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId] ?? { items: [] as Message[] };
        const existingIds = new Set(slice.items.map((m) => m._id));
        const newItems = action.connection.items.filter((m) => !existingIds.has(m._id));

        const merged = this.computeGroupedMessages([...slice.items, ...newItems]);

        this.patchChannel(ctx, action.channelId, {
            items: merged,
            nextToken: action.connection.nextToken,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
        });
    }

    @Action(LoadMoreMessagesError)
    loadMoreMessagesError(ctx: StateContext<MessageStateModel>, action: LoadMoreMessagesError) {
        this.patchChannel(ctx, action.channelId, { loading: false, error: action.error });
    }

    @Action(SendMessage)
    send(ctx: StateContext<MessageStateModel>, action: SendMessage) {
        const optimisticId = `optimistic-${Date.now()}`;
        const optimistic: Message = {
            _id: optimisticId,
            channelId: action.channelId,
            content: action.content,
            files: action.files,
            isFromBot: action.isFromBot,
            sender: {
                _id: this.currentUser.cognitoId,
                firstName: this.currentUser.firstName,
                lastName: this.currentUser.lastName,
                avatar: this.currentUser.profilePhotoLink,
            },
            senderId: this.currentUser.cognitoId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        const st = ctx.getState();
        const slice = st.byChannel[action.channelId] ?? {
            items: [],
            loading: false,
            error: null as unknown,
            lastUpdated: 0,
        };
        ctx.patchState({
            byChannel: {
                ...st.byChannel,
                [action.channelId]: {
                    ...slice,
                    items: this.computeGroupedMessages([optimistic, ...slice.items]),
                },
            },
        });

        return this.chatService
            .sendMessage({
                channelId: action.channelId,
                content: action.content,
                files: action.files,
                isFromBot: action.isFromBot,
            })
            .pipe(
                tap((message) =>
                    ctx.dispatch(new SendMessageSuccess(action.channelId, message, optimisticId)),
                ),
                catchError((error) => {
                    ctx.dispatch(new SendMessageError(action.channelId, error, optimisticId));
                    return of(error);
                }),
            );
    }

    @Action(SendMessageSuccess)
    sendMessageSuccess(ctx: StateContext<MessageStateModel>, action: SendMessageSuccess) {
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId] ?? { items: [] as Message[] };
        const replaced = (slice.items || []).map((m) =>
            m._id === action.optimisticId ? action.message : m,
        );
        // The onNewMessage subscription fires for the sender too and may have already
        // inserted this same real message via AddMessage before this handler runs. After
        // swapping the optimistic placeholder for the real message, drop any duplicate
        // real entry so the optimistic-vs-subscription race can't leave two copies.
        const items = this.computeGroupedMessages(this.dedupeById(replaced));
        this.patchChannel(ctx, action.channelId, { items, lastUpdated: Date.now() });

        // Persist lastSenderId to DynamoDB immediately after a successful send.
        // This is the reliable path — the stream handler also does this, but may
        // fail silently. Calling updateChannel from the frontend guarantees the
        // DynamoDB channel record is always up-to-date.
        const senderId = action.message.senderId ?? this.currentUser.cognitoId;
        const now = new Date().toISOString();

        // 1. Update local store immediately (no backend round-trip needed for display)
        this.store.dispatch(
            new UpdateChannel({
                _id: action.channelId,
                lastSenderId: senderId,
                updatedAt: now,
            } as any),
        );

        // 2. Persist to DynamoDB via AppSync mutation (fire-and-forget)
        this.chatService
            .updateChannel({ channelId: action.channelId, lastSenderId: senderId })
            .pipe(catchError((err) => { console.error('[sendMessageSuccess] updateChannel error:', err); return of(null); }))
            .subscribe();
    }

    @Action(SendMessageError)
    sendMessageError(ctx: StateContext<MessageStateModel>, action: SendMessageError) {
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId] ?? { items: [] as Message[] };
        const items = (slice.items || []).filter((m) => m._id !== action.optimisticId);
        this.patchChannel(ctx, action.channelId, { items, error: action.error });
    }

    @Action(ClearMessagesForChannel)
    clearMessagesForChannel(ctx: StateContext<MessageStateModel>, action: ClearMessagesForChannel) {
        const state = ctx.getState();
        const { [action.channelId]: _removed, ...rest } = state.byChannel;
        ctx.patchState({ byChannel: rest });
    }

    @Action(AddMessage)
    addMessage(ctx: StateContext<MessageStateModel>, action: AddMessage) {
        const state = ctx.getState();
        const slice = state.byChannel[action.channelId] ?? {
            items: [],
            loading: false,
            error: null as unknown,
            lastUpdated: 0,
        };

        // Check if message already exists to avoid duplicates
        const existingMessage = slice.items.find((m) => m._id === action.message._id);
        if (existingMessage) {
            return;
        }

        // If this is the current user's own message arriving over the subscription, it
        // is likely still shown as an optimistic placeholder (the onNewMessage push can
        // beat the sendMessage mutation response). Replace that placeholder in place
        // instead of prepending a second copy, so the optimistic-vs-subscription race
        // can't briefly render two bubbles for a single send.
        const optimisticIndex =
            action.message.senderId === this.currentUser.cognitoId
                ? slice.items.findIndex(
                      (m) =>
                          m._id?.startsWith('optimistic-') &&
                          m.senderId === action.message.senderId &&
                          (m.content ?? '') === (action.message.content ?? '') &&
                          JSON.stringify(m.files ?? []) === JSON.stringify(action.message.files ?? []),
                  )
                : -1;

        const merged =
            optimisticIndex === -1
                ? [action.message, ...slice.items]
                : slice.items.map((m, i) => (i === optimisticIndex ? action.message : m));

        // dedupeById guards against the placeholder already having been swapped for the
        // real message by SendMessageSuccess (newest first preserved by computeGroupedMessages).
        const items = this.computeGroupedMessages(this.dedupeById(merged));

        ctx.patchState({
            byChannel: {
                ...state.byChannel,
                [action.channelId]: {
                    ...slice,
                    items,
                    lastUpdated: Date.now(),
                },
            },
        });
    }

    // Helpers
    private ensureChannel(ctx: StateContext<MessageStateModel>, channelId: string) {
        const state = ctx.getState();
        if (!state.byChannel[channelId]) {
            ctx.patchState({
                byChannel: {
                    ...state.byChannel,
                    [channelId]: {
                        items: [],
                        nextToken: undefined,
                        loading: false,
                        error: null,
                        lastUpdated: 0,
                    },
                },
            });
        }
    }

    private patchChannel(
        ctx: StateContext<MessageStateModel>,
        channelId: string,
        patch: Partial<MessageStateModel['byChannel'][string]>,
    ) {
        const state = ctx.getState();
        const slice = state.byChannel[channelId] ?? {
            items: [],
            nextToken: undefined,
            loading: false,
            error: null,
            lastUpdated: 0,
        };
        ctx.patchState({
            byChannel: {
                ...state.byChannel,
                [channelId]: { ...slice, ...patch },
            },
        });
    }

    private dedupeById(messages: Message[]): Message[] {
        const seen = new Set<string>();
        return messages.filter((m) => {
            if (seen.has(m._id)) return false;
            seen.add(m._id);
            return true;
        });
    }

    private computeGroupedMessages(messages: Message[]): Message[] {
        if (!messages || messages.length === 0) return [];

        const byId: Record<string, { isLastOfGroup: boolean; date?: string }> = {};
        const sorted = [...messages].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );

        for (let i = 0; i < sorted.length; i++) {
            const current = sorted[i];
            const prev = i > 0 ? sorted[i - 1] : undefined;

            const currentTs = new Date(current.createdAt).getTime();
            const prevTs = prev ? new Date(prev.createdAt).getTime() : 0;
            const messageIntervalTime = 60000 * 5; // 5 minutes

            let isLastOfGroup = true;
            if (prev) {
                const senderChanged = prev.senderId !== current.senderId;
                const crossedInterval = currentTs - prevTs > messageIntervalTime;
                const prevIsBot = !!prev.isFromBot;

                isLastOfGroup = senderChanged || crossedInterval || prevIsBot;
            }

            byId[current._id] = {
                isLastOfGroup,
                date: format(new Date(current.createdAt), 'MM/dd/yyyy'),
            };
        }

        // Map the computed flags/dates back to the original order to preserve display order
        return messages
            .map((m) => ({
                ...m,
                isLastOfGroup: byId[m._id]?.isLastOfGroup ?? true,
                date: byId[m._id]?.date ?? format(new Date(m.createdAt), 'MM/dd/yyyy'),
            }))
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
}
